import React, { useEffect, useState, lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Link, Switch, Route, Redirect, useRouteMatch } from 'react-router-dom';
import { AppSidebar, SidebarContext } from './components/AppSidebar';
import { Topbar } from './components/Topbar';
import { canManageUsers, getAgentLandingPath, getSingleAssignedAgentWithoutImageIPath, hasAgentLicense, isAgentOnlyAccount } from './lib/access';

// Pages
import { Overview }               from './pages/overview';
import { FinancePage }            from './pages/finance';
import { SalesPage }              from './pages/sales';
import { OperationsPage }         from './pages/operations';
import { HrPage }                 from './pages/hr';
import { SupplyPage }             from './pages/supply';
import { CustomerPage }           from './pages/customer';
import { ReconciliationPage }     from './pages/reconciliation';
import { HistoricalPage }         from './pages/historical';
import { BenchmarksPage }         from './pages/benchmarks';
import { AgentsPage }             from './pages/agents';
import { ReportsPage }            from './pages/reports';
import { ActivityReportPage }     from './pages/activity-report';
import { OnboardingPage }         from './pages/onboarding';
import { TrainingPage }           from './pages/training';
import { ProfilePage }            from './pages/profile';
import { SettingsPage }           from './pages/settings';
import { SubscriptionManagementPage } from './pages/admin-subscriptions';
import { UserAllocationPage }     from './pages/admin-users';
import { ReportAllocationPage }   from './pages/admin-reports';
import { InsightHubReportPage }   from './pages/report-viewer';

// Agent components (lazy-loaded so they don't bloat the main bundle)
const ChatWidgetPage       = lazy(() => import('../agents/chatwidget/ChatWidgetPage'));
const DocumentAgentPage    = lazy(() => import('../agents/documentagent/DocumentAgentPage'));
const SpreadsheetAIModule  = lazy(() => import('../agents/spreadsheetai/SpreadsheetAIPage'));
const LabourLawAdvisorPage = lazy(() => import('../agents/labourlawadvisor/LabourLawAdvisorPage'));
const TaxAgentPage         = lazy(() => import('../agents/taxagent/TaxAgentPage'));
const ShippingDocumentAgent = lazy(() => import('../components/ShippingDocumentAgent'));
const ComplianceAgent      = lazy(() => import('../components/ComplianceAgent'));

const builtInCustomAgentPages = {
  chat_widget: ChatWidgetPage,
  document_agent: DocumentAgentPage,
  spreadsheet_ai: SpreadsheetAIModule,
  labour_law_advisor: LabourLawAdvisorPage,
  tax_agent: TaxAgentPage,
  shipping_document_agent: ShippingDocumentAgent,
  compliance_agent: ComplianceAgent,
};

function AgentRoute({ component: Component, agentCode, legacyKey, ...rest }) {
  const account = useSelector((state) => state.account || {});

  return (
    <Route
      {...rest}
      render={(props) =>
        hasAgentLicense(account, agentCode, legacyKey) ? (
          <Component {...props} />
        ) : (
          <Redirect to={getAgentLandingPath(account)} />
        )
      }
    />
  );
}

function CustomAgentRoute({ component: Component, ...rest }) {
  const account = useSelector((state) => state.account || {});

  return (
    <Route
      {...rest}
      render={(props) => {
        const agentCode = props.match?.params?.agentCode;
        return hasAgentLicense(account, agentCode) ? (
          <Component {...props} />
        ) : (
          <Redirect to={getAgentLandingPath(account)} />
        );
      }}
    />
  );
}

function toPascalCase(code = '') {
  return String(code)
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

function MissingCustomAgentPage({ agentCode }) {
  return (
    <div style={{ padding: 40 }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>Agent frontend not configured</h1>
      <p style={{ marginTop: 12, color: 'var(--color-muted-foreground)' }}>
        No frontend file was found for {agentCode}. Add frontend code from Manage Subscription and rebuild/restart the frontend.
      </p>
    </div>
  );
}

function CustomAgentPageLoader({ match }) {
  const agentCode = match?.params?.agentCode || '';
  const BuiltInAgentPage = builtInCustomAgentPages[agentCode];
  const [CustomPage, setCustomPage] = useState(null);

  useEffect(() => {
    if (BuiltInAgentPage) return undefined;

    let mounted = true;
    const folderName = agentCode.toLowerCase().trim().replace(/_/g, '');
    const componentName = `${toPascalCase(agentCode)}Page`;

    setCustomPage(null);

    import(
      /* webpackInclude: /Page\.js$/ */
      `../agents/${folderName}/${componentName}.js`
    )
      .then((module) => {
        if (!mounted) return;
        setCustomPage(() => module.default || (() => <MissingCustomAgentPage agentCode={agentCode} />));
      })
      .catch(() => {
        if (!mounted) return;
        setCustomPage(() => () => <MissingCustomAgentPage agentCode={agentCode} />);
      });

    return () => {
      mounted = false;
    };
  }, [agentCode, BuiltInAgentPage]);

  if (BuiltInAgentPage) {
    return <BuiltInAgentPage />;
  }

  if (!CustomPage) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-muted-foreground)' }}>Loading...</div>;
  }

  return (
    <div style={{ padding: '24px 32px' }}>
      <Link
        to="/imagei/agents"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--color-muted-foreground)',
          fontSize: 14,
          fontWeight: 500,
          textDecoration: 'none',
          marginBottom: 18,
        }}
      >
        <span aria-hidden="true">←</span>
        <span>All agents</span>
      </Link>
      <CustomPage />
    </div>
  );
}

function ImageIRoute({ component: Component, ...rest }) {
  const account = useSelector((state) => state.account || {});

  return (
    <Route
      {...rest}
      render={(props) => {
        const singleAgentPath = getSingleAssignedAgentWithoutImageIPath(account);
        return singleAgentPath || isAgentOnlyAccount(account) ? (
          <Redirect to={singleAgentPath || getAgentLandingPath(account)} />
        ) : (
          <Component {...props} />
        );
      }}
    />
  );
}

function OpenImageIRoute({ component: Component, ...rest }) {
  return (
    <Route
      {...rest}
      render={(props) => <Component {...props} />}
    />
  );
}

function AgentIndexRoute({ component: Component, ...rest }) {
  const account = useSelector((state) => state.account || {});

  return (
    <Route
      {...rest}
      render={(props) => {
        const landingPath = getSingleAssignedAgentWithoutImageIPath(account) || getAgentLandingPath(account);
        return landingPath !== '/imagei/agents' ? <Redirect to={landingPath} /> : <Component {...props} />;
      }}
    />
  );
}

function UserAllocationRoute({ component: Component, ...rest }) {
  const account = useSelector((state) => state.account || {});

  return (
    <Route
      {...rest}
      render={(props) =>
        canManageUsers(account) ? (
          <Component {...props} />
        ) : (
          <Redirect to={getSingleAssignedAgentWithoutImageIPath(account) || getAgentLandingPath(account)} />
        )
      }
    />
  );
}

function SuperAdminRoute({ component: Component, ...rest }) {
  const account = useSelector((state) => state.account || {});

  return (
    <Route
      {...rest}
      render={(props) =>
        canManageUsers(account) ? (
          <Component {...props} />
        ) : (
          <Redirect to={getSingleAssignedAgentWithoutImageIPath(account) || getAgentLandingPath(account)} />
        )
      }
    />
  );
}

function RootRedirect({ basePath }) {
  const account = useSelector((state) => state.account || {});
  const singleAgentPath = getSingleAssignedAgentWithoutImageIPath(account);
  return <Redirect to={singleAgentPath || (isAgentOnlyAccount(account) ? getAgentLandingPath(account) : `${basePath}/overview`)} />;
}

export default function InsightHubLayout({ user, onLogout }) {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });
  const { path } = useRouteMatch();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed }}>
      <div className="ih-root" style={{ display: 'flex', height: '100svh', width: '100%', overflow: 'hidden', fontFamily: "'Inter', system-ui, sans-serif" }}>
        <AppSidebar />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
          <Topbar user={user} onLogout={onLogout} />
          <main style={{ flex: 1, overflowY: 'auto', background: 'var(--background)' }}>
            <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: 'var(--color-muted-foreground)' }}>Loading…</div>}>
              <Switch>
                <ImageIRoute exact path={`${path}/overview`}       component={Overview} />
                <ImageIRoute exact path={`${path}/finance`}        component={FinancePage} />
                <ImageIRoute exact path={`${path}/sales`}          component={SalesPage} />
                <ImageIRoute exact path={`${path}/operations`}     component={OperationsPage} />
                <ImageIRoute exact path={`${path}/hr`}             component={HrPage} />
                <ImageIRoute exact path={`${path}/supply`}         component={SupplyPage} />
                <ImageIRoute exact path={`${path}/customer`}       component={CustomerPage} />
                <ImageIRoute exact path={`${path}/reconciliation`} component={ReconciliationPage} />
                <ImageIRoute exact path={`${path}/historical`}     component={HistoricalPage} />
                <ImageIRoute exact path={`${path}/benchmarks`}     component={BenchmarksPage} />
                <AgentIndexRoute exact path={`${path}/agents`} component={AgentsPage} />
                <ImageIRoute exact path={`${path}/reports`}        component={ReportsPage} />
                <ImageIRoute exact path={`${path}/activity-report`} component={ActivityReportPage} />
                <ImageIRoute exact path={`${path}/onboarding`}     component={OnboardingPage} />
                <ImageIRoute exact path={`${path}/training`}       component={TrainingPage} />
                <OpenImageIRoute exact path={`${path}/profile`}        component={ProfilePage} />
                <OpenImageIRoute exact path={`${path}/settings`}       component={SettingsPage} />
                <ImageIRoute exact path={`${path}/admin/subscriptions`} component={SubscriptionManagementPage} />
                <UserAllocationRoute exact path={`${path}/admin/users`}    component={UserAllocationPage} />
                <SuperAdminRoute exact path={`${path}/admin/reports`}  component={ReportAllocationPage} />
                <ImageIRoute exact path={`${path}/report`}         component={InsightHubReportPage} />
                {/* Agent routes — rendered inside Insight Hub chrome */}
                <AgentRoute exact path={`${path}/agents/chat`}        component={ChatWidgetPage} agentCode="chat_widget" legacyKey="license_allocated_chatwidget" />
                <AgentRoute exact path={`${path}/agents/document`}    component={DocumentAgentPage} agentCode="document_agent" legacyKey="license_allocated_document_agent" />
                <AgentRoute exact path={`${path}/agents/shipping`}    component={ShippingDocumentAgent} agentCode="shipping_document_agent" legacyKey="license_allocated_shipping_document_agent" />
                <AgentRoute exact path={`${path}/agents/spreadsheet`} component={SpreadsheetAIModule} agentCode="spreadsheet_ai" legacyKey="license_allocated_spreadsheet_ai" />
                <AgentRoute exact path={`${path}/agents/labour`}      component={LabourLawAdvisorPage} agentCode="labour_law_advisor" legacyKey="license_allocated_labour_law_advisor" />
                <AgentRoute exact path={`${path}/agents/compliance`}  component={ComplianceAgent} agentCode="compliance_agent" legacyKey="license_allocated_compliance_agent" />
                <AgentRoute exact path={`${path}/agents/tax`}         component={TaxAgentPage} agentCode="tax_agent" legacyKey="license_allocated_tax_agent" />
                <CustomAgentRoute exact path={`${path}/agents/custom/:agentCode`} component={CustomAgentPageLoader} />
                <Route exact path={path} render={() => <RootRedirect basePath={path} />} />
                <Redirect to={`${path}/overview`} />
              </Switch>
            </Suspense>
          </main>
        </div>
      </div>
    </SidebarContext.Provider>
  );
}
